<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import {
  CPUS,
  FAN_WATTS,
  GPUS,
  MEMORY_TYPES,
  MEMORY_WATTS,
  type Platform,
  PLATFORM_WATTS,
  PLATFORMS,
  STORAGE_TYPES,
  STORAGE_WATTS,
  type StorageType,
} from '../model/parts'
import { type Build, LIMITS, type PartLine } from '../model/spec'
import CountSelect from './CountSelect.vue'
import FormRow from './FormRow.vue'
import FormSection from './FormSection.vue'
import ModelPicker from './ModelPicker.vue'
import OtherWattsRow from './OtherWattsRow.vue'
import PartLinePicker from './PartLinePicker.vue'

/** The hardware: everything that draws from the power supply. */
const props = defineProps<{ modelValue: Build }>()
const emit = defineEmits<{ 'update:modelValue': [Build] }>()

const { t, n } = useI18n()

function update<K extends keyof Build>(key: K, value: Build[K]): void {
  emit('update:modelValue', { ...props.modelValue, [key]: value })
}

function updateStorage(row: 0 | 1, line: PartLine<StorageType>): void {
  const [first, second] = props.modelValue.storage
  update('storage', row === 0 ? [line, second] : [first, line])
}

function onPlatform(event: Event): void {
  const target = event.target
  if (!(target instanceof HTMLSelectElement)) return
  const platform = PLATFORMS.find((candidate) => candidate === target.value)
  if (platform) update('platform', platform)
}

const platformLabel = (platform: Platform): string =>
  `${t(`platform.${platform}`)} — ${t('form.watts', { value: n(PLATFORM_WATTS[platform]) })}`
</script>

<template>
  <FormSection :legend="t('form.components')">
    <FormRow :label="t('form.platform')" for-id="platform">
      <select id="platform" :value="modelValue.platform" @change="onPlatform">
        <option v-for="platform in PLATFORMS" :key="platform" :value="platform">
          {{ platformLabel(platform) }}
        </option>
      </select>
    </FormRow>

    <ModelPicker
      id="cpu"
      :model-value="modelValue.cpu"
      :label="t('form.cpu')"
      :catalogue="CPUS"
      :max-count="LIMITS.cpuCount"
      @update:model-value="(cpu) => update('cpu', cpu)"
    />

    <ModelPicker
      id="gpu"
      :model-value="modelValue.gpu"
      :label="t('form.gpu')"
      :catalogue="GPUS"
      :max-count="LIMITS.gpuCount"
      allow-none
      @update:model-value="(gpu) => update('gpu', gpu)"
    />

    <PartLinePicker
      id="memory"
      :model-value="modelValue.memory"
      :label="t('form.memory')"
      :types="MEMORY_TYPES"
      label-prefix="memory"
      :watts="MEMORY_WATTS"
      :max="LIMITS.memory"
      @update:model-value="(memory) => update('memory', memory)"
    />

    <PartLinePicker
      id="storage"
      :model-value="modelValue.storage[0]"
      :label="t('form.storage')"
      :types="STORAGE_TYPES"
      label-prefix="storage"
      :watts="STORAGE_WATTS"
      :max="LIMITS.storage"
      @update:model-value="(line) => updateStorage(0, line)"
    />

    <PartLinePicker
      id="storage-more"
      :model-value="modelValue.storage[1]"
      :label="t('form.storageMore')"
      :types="STORAGE_TYPES"
      label-prefix="storage"
      :watts="STORAGE_WATTS"
      :max="LIMITS.storage"
      hide-label
      @update:model-value="(line) => updateStorage(1, line)"
    />

    <FormRow :label="t('form.fans')" for-id="fans">
      <CountSelect
        id="fans"
        :model-value="modelValue.fans"
        :max="LIMITS.fans"
        :label="t('form.fans')"
        @update:model-value="(count) => update('fans', count)"
      />
      <span class="form-row__muted">
        {{ t('form.each', { watts: t('form.watts', { value: n(FAN_WATTS) }) }) }}
      </span>
    </FormRow>

    <OtherWattsRow
      :model-value="modelValue.otherWatts"
      @update:model-value="(watts) => update('otherWatts', watts)"
    />
  </FormSection>
</template>
